/**
 * Inline progress line for a single tool call.
 *
 * Renders as plain text alongside the assistant's prose — no bordered card —
 * so progress and conversation share a single visual column. Inputs appear
 * as an indented list under the line, and sub-agent calls are rendered
 * recursively with a small left indent.
 *
 * Spinner only shows while running; on complete the status mark collapses
 * to a subtle check so stale progress lines fade out of foreground attention.
 */

import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { z } from 'zod';

import { Markdown } from './Markdown';
import type { EpiToolCall } from '../types/epi-chat-types';
import {
  extractAvNameFromPath,
  friendlyToolLabel,
  isInternalArtifactPath,
  slowToolHint,
} from '../utils/friendlyToolLabel';

/**
 * OR-6456: ``find_concept_group``'s structured result — a compact
 * status/candidates shape instead of a free-text write-up. A replayed
 * pre-recall-v2 session still sends a plain string, which this schema simply
 * won't match — {@link parseRecallResult} falls back to generic rendering in
 * that case.
 */
const RecallResultSchema = z.object({
  status: z.enum(['found', 'ambiguous', 'not_found', 'parse_error']),
  candidates: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      concept_count: z.number().nullable(),
    })
  ),
  recommendation: z.string().nullable(),
  reasoning: z.string().optional(),
});

/**
 * Only ever applies to ``find_concept_group``, and only when its result is
 * already an object (the v2 shape) — a string result (flag-off, or a replayed
 * message from before the flag was on) simply doesn't match and falls back to
 * this component's generic rendering below.
 */
const parseRecallResult = (toolCall: EpiToolCall) => {
  if (toolCall.toolName !== 'find_concept_group') return null;
  const parsed = RecallResultSchema.safeParse(toolCall.result);
  return parsed.success ? parsed.data : null;
};

const recallSummaryLine = (result: z.infer<typeof RecallResultSchema>): string | null => {
  switch (result.status) {
    case 'found':
      return result.candidates.length === 1
        ? `Found: ${result.candidates[0].name || result.candidates[0].id}`
        : `Found ${result.candidates.length} groups to bind together`;
    case 'ambiguous':
      return `${result.candidates.length} candidates — researcher choice needed`;
    case 'not_found':
      return 'No matching concept group found';
    default:
      // parse_error — a rare defensive fallback; let the generic renderer
      // show it (useful for debugging) rather than claiming a clean outcome.
      return null;
  }
};

interface ToolCallCardProps {
  toolCall: EpiToolCall;
  depth?: number;
  /**
   * OR-6328 (``research_assistant_hide_tool_internals``): render only the
   * plain-language label per step — no raw tool name, no args/result
   * expansion, no sub-agent reasoning rows. The one carve-out is a
   * researcher-facing ``reason`` arg (e.g. the execute-SQL tool's "Reason"
   * text), which stays expandable on its own. Sub-calls still render (their
   * labels are plain language too). Presentation-only — the durable ui#
   * events / telemetry behind these rows are unaffected.
   * TODO: cleanup with research_assistant_hide_tool_internals ff
   */
  hideInternals?: boolean;
}

const MAX_ARG_VALUE_LEN = 140;
const MAX_RESULT_LEN = 400;

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') {
    return value.length > MAX_ARG_VALUE_LEN ? `${value.slice(0, MAX_ARG_VALUE_LEN)}…` : value;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    const joined = value.map((v) => (typeof v === 'string' ? v : JSON.stringify(v))).join(', ');
    return joined.length > MAX_ARG_VALUE_LEN ? `${joined.slice(0, MAX_ARG_VALUE_LEN)}…` : joined;
  }
  if (typeof value === 'object') {
    const json = JSON.stringify(value);
    return json.length > MAX_ARG_VALUE_LEN ? `${json.slice(0, MAX_ARG_VALUE_LEN)}…` : json;
  }
  return String(value);
};

const formatResult = (result: unknown): string | null => {
  if (result === null || result === undefined) return null;
  const raw = typeof result === 'string' ? result : JSON.stringify(result, null, 2);
  return raw.length > MAX_RESULT_LEN ? `${raw.slice(0, MAX_RESULT_LEN)}…` : raw;
};

/**
 * Hide backend-only routing args from the visible card. Anything matching
 * `avs/<slug>/draft.sql` (and the manifest/explain siblings) is an
 * implementation-detail path the epi has no use for; the friendly label
 * already surfaced the analytical variable name. Likewise the legacy `sql_or_path` key is
 * suppressed even when the value is a literal SQL string — the card row
 * is for orientation, not SQL inspection (the SQL tab exists for that).
 */
const HIDDEN_ARG_KEYS = new Set(['sql_or_path']);

const isHiddenArg = (key: string, value: unknown): boolean => {
  if (HIDDEN_ARG_KEYS.has(key)) return true;
  if (isInternalArtifactPath(value)) return true;
  return false;
};

/**
 * For args we DO show, normalise any backend artifact path embedded in
 * the value (e.g. inside a `path` arg) to the bare analytical variable name so the visible
 * card never leaks `avs/<name>/draft.sql`.
 */
const normaliseDisplayArg = (_key: string, value: unknown): unknown => {
  if (typeof value === 'string') return extractAvNameFromPath(value);
  return value;
};

/**
 * User-facing rename of arg keys whose internal name leaks
 * implementation detail. The backend keeps the field as `slug` for
 * compatibility with stored artifacts and tool signatures; in chat the
 * epi sees this as the analytical variable's "name". Same goes for `sql_or_path` (already
 * suppressed entirely via {@link HIDDEN_ARG_KEYS}, kept here for safety).
 */
const ARG_KEY_DISPLAY: Record<string, string> = {
  slug: 'name',
  av_slug: 'name',
  anchor_slug: 'anchor',
};

const displayArgKey = (key: string): string => ARG_KEY_DISPLAY[key] ?? key;

export const ToolCallCard = ({ toolCall, depth = 0, hideInternals = false }: ToolCallCardProps) => {
  // Compute the live progress line — the "currently doing X" badge that
  // shows next to a parent tool while a sub-agent is mid-flight. Derived
  // from the most recently started sub-call that's still running. Empty
  // when nothing is running (clears after the last sub-call completes).
  const lastRunningSubCall = [...(toolCall.subCalls ?? [])]
    .reverse()
    .find((sub) => sub.status === 'running');
  // Effective status — the call itself OR any descendant sub-call still
  // running. Strands fires TOOL_CALL_END for the parent's tool result
  // separately from each sub-agent tool's TOOL_CALL_END, and arrival
  // order is not guaranteed: the parent's end can land before a child's
  // end, which used to flip the parent's icon to ✓ while the child was
  // still spinning. Recursing keeps the spinner on until the whole tree
  // is settled, matching the user's mental model that the work isn't
  // done until everything underneath is done.
  const isAnyDescendantRunning = (call: EpiToolCall): boolean =>
    call.status === 'running' || (call.subCalls ?? []).some(isAnyDescendantRunning);
  const effectiveStatus: EpiToolCall['status'] = isAnyDescendantRunning(toolCall)
    ? 'running'
    : toolCall.status;
  const progressLine =
    effectiveStatus === 'running' && lastRunningSubCall
      ? friendlyToolLabel(lastRunningSubCall.toolName, lastRunningSubCall.args, {
          hideInternals,
        }) || lastRunningSubCall.friendlyLabel
      : null;
  const slowHint = effectiveStatus === 'running' ? slowToolHint(toolCall.toolName) : null;
  // Render the heterogeneous timeline (tool calls + reasoning interleaved
  // in arrival order) when the sub-agent has emitted any reasoning. Falls
  // back to the simple subCalls list when there's no reasoning to mix in.
  const subCallsById = new Map((toolCall.subCalls ?? []).map((sub) => [sub.toolId, sub]));
  const timeline = toolCall.internalLog ?? [];
  const hasReasoningInLog = timeline.some((entry) => entry.kind === 'reasoning');
  const argEntries = Object.entries(toolCall.args)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .filter(([key, value]) => !isHiddenArg(key, value))
    .map(([key, value]) => [key, normaliseDisplayArg(key, value)] as [string, unknown]);
  const resultText = formatResult(toolCall.result);
  // OR-6456: find_concept_group's structured result, when present. Drives an
  // always-visible plain-language outcome line (below) and a leaner expanded
  // detail (reasoning instead of a raw JSON dump) — regardless of hideInternals
  // for the summary, but reasoning only when internals are NOT hidden (AC6).
  const recallResult = parseRecallResult(toolCall);
  const recallSummary = recallResult ? recallSummaryLine(recallResult) : null;
  // Recompute the label from live args so that when Strands streams the tool's
  // `input` JSON in late, the row updates from "Recalling prior findings" to
  // "Recalling prior findings for <query>" and multiple same-tool calls
  // disambiguate visually. Falls back to the precomputed label.
  // With internals hidden, an uncurated tool renders its base label only —
  // the generic label fallback would otherwise put the first argument value
  // (raw SQL, paths) straight into the row text.
  const liveLabel =
    friendlyToolLabel(toolCall.toolName, toolCall.args, { hideInternals }) ||
    toolCall.friendlyLabel;
  // OR-6328 carve-out: a ``reason`` arg is researcher-facing prose (the
  // execute-SQL tool's "Reason" text, the free-form drafter's rationale) —
  // the ONE detail still expandable when internals are hidden.
  // TODO: cleanup with research_assistant_hide_tool_internals ff
  const reasonText =
    typeof toolCall.args.reason === 'string' && toolCall.args.reason.trim().length > 0
      ? toolCall.args.reason
      : null;
  const hasDetails = hideInternals ? reasonText !== null : argEntries.length > 0 || !!resultText;
  // Flag OFF: the row is always toggleable (legacy behavior, even with no
  // details to show). Flag ON: expandable only for the reason carve-out.
  const expandable = hideInternals ? hasDetails : true;
  // OR-6201: collapsed by default (even while running) and sticky once
  // opened — matches the parent ToolCallGroupCard. The ephemeral progress
  // line below still surfaces the live sub-step without expanding, so a
  // collapsed row is never a blind row, and a user who opens a row to
  // watch the tail isn't yanked shut when the step completes.
  const [expanded, setExpanded] = useState(false);

  const statusMark =
    effectiveStatus === 'running' ? (
      <CircularProgress size={10} thickness={5} />
    ) : effectiveStatus === 'error' ? (
      <ErrorOutlineIcon sx={{ fontSize: '0.8rem' }} color="error" />
    ) : (
      <CheckRoundedIcon sx={{ fontSize: '0.8rem', color: 'success.main' }} />
    );

  return (
    <Box sx={{ marginLeft: depth > 0 ? 2 : 0, marginY: 0.25 }}>
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        // Flag OFF keeps the legacy always-toggleable row; flag ON allows
        // expansion only when there is a researcher-facing detail to show.
        onClick={expandable ? () => setExpanded((v) => !v) : undefined}
        sx={{
          cursor: expandable ? 'pointer' : 'default',
          userSelect: 'none',
          color: 'text.secondary',
          minHeight: 20,
          '&:hover .tool-caret': { color: 'text.primary' },
        }}
      >
        {statusMark}
        <Typography
          component="span"
          variant="body2"
          sx={{
            fontStyle: 'italic',
            color: effectiveStatus === 'complete' ? 'text.secondary' : 'text.primary',
          }}
        >
          {liveLabel}
        </Typography>
        {/* Raw tool identifier — developer detail, hidden for researchers
            under OR-6328. TODO: cleanup with research_assistant_hide_tool_internals ff */}
        {!hideInternals && (
          <Typography
            component="span"
            variant="caption"
            sx={{
              fontFamily: 'Menlo, Consolas, monospace',
              color: 'text.secondary',
            }}
          >
            {toolCall.toolName}
          </Typography>
        )}
        {expandable && (
          <Typography
            component="span"
            variant="caption"
            className="tool-caret"
            sx={{
              color: 'text.secondary',
              marginLeft: 'auto',
              paddingLeft: 1,
            }}
            aria-label={expanded ? 'Collapse details' : 'Expand details'}
          >
            {expanded ? '▾' : '▸'}
          </Typography>
        )}
      </Stack>

      {progressLine && (
        // OR-5964 progress surface — ephemeral live readout while a
        // sub-agent runs. Replaces in place; never accumulates. Visible
        // even with the drill-in toggle off so the user can see the
        // agent making progress without being shown the internal steps.
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ marginLeft: 2.25, marginTop: 0.25, color: 'text.secondary' }}
          data-testid={`tool-progress-${toolCall.toolId}`}
        >
          <Typography
            component="span"
            variant="caption"
            sx={{
              fontWeight: 600,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: 'text.disabled',
            }}
          >
            …
          </Typography>
          <Typography
            component="span"
            variant="caption"
            sx={{ color: 'text.secondary', fontStyle: 'italic' }}
          >
            {progressLine}
          </Typography>
        </Stack>
      )}

      {slowHint && (
        <Typography
          component="div"
          variant="caption"
          sx={{
            marginLeft: 2.25,
            marginTop: 0.25,
            color: 'text.secondary',
            fontStyle: 'italic',
          }}
          data-testid={`tool-slow-hint-${toolCall.toolId}`}
        >
          {slowHint}
        </Typography>
      )}

      {recallSummary && (
        // OR-6456: plain-language outcome, visible regardless of hideInternals —
        // mirrors slowHint/bestEffortFailureNote's always-shown treatment below.
        <Typography
          component="div"
          variant="body2"
          sx={{ marginLeft: 2.25, marginTop: 0.25, color: 'text.secondary' }}
          data-testid={`tool-recall-summary-${toolCall.toolId}`}
        >
          {recallSummary}
        </Typography>
      )}

      {toolCall.bestEffortFailureNote && (
        // OR-6324/OR-6328 — best-effort failure note. Plain language, no
        // internals, so it renders in BOTH flag states: safe for
        // researchers, and the raw cause lives in telemetry for us. Not an
        // error state — the assistant continued without the result.
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{ marginLeft: 2.25, marginTop: 0.25 }}
          data-testid={`tool-failure-note-${toolCall.toolId}`}
        >
          <WarningAmberRoundedIcon sx={{ fontSize: '0.8rem' }} color="warning" />
          <Typography
            component="span"
            variant="caption"
            sx={{ color: 'text.secondary', fontStyle: 'italic' }}
          >
            {toolCall.bestEffortFailureNote}
          </Typography>
        </Stack>
      )}

      {expanded && hideInternals && reasonText && (
        // OR-6328 reason carve-out — the only expandable detail flag-ON.
        // TODO: cleanup with research_assistant_hide_tool_internals ff
        <Box
          sx={{ marginLeft: 2.25, marginTop: 0.25 }}
          data-testid={`tool-reason-${toolCall.toolId}`}
        >
          <Typography component="div" variant="body2" sx={{ color: 'text.secondary' }}>
            <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Reason
            </Box>
            {': '}
            {reasonText}
          </Typography>
        </Box>
      )}

      {expanded && !hideInternals && recallResult?.reasoning && (
        // OR-6456: the lean reasoning field, in place of the generic raw-object
        // dump below — only when internals are NOT hidden (AC6).
        <Box
          sx={{ marginLeft: 2.25, marginTop: 0.25 }}
          data-testid={`tool-recall-reasoning-${toolCall.toolId}`}
        >
          <Typography component="div" variant="body2" sx={{ color: 'text.secondary' }}>
            <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Reasoning
            </Box>
            {': '}
            {recallResult.reasoning}
          </Typography>
        </Box>
      )}

      {expanded && !hideInternals && !recallResult && hasDetails && (
        <Box sx={{ marginLeft: 2.25, marginTop: 0.25 }}>
          {argEntries.map(([key, value]) => (
            <Typography key={key} component="div" variant="body2" sx={{ color: 'text.secondary' }}>
              <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {displayArgKey(key)}
              </Box>
              {': '}
              <Box
                component="span"
                sx={{
                  fontFamily: 'Menlo, Consolas, monospace',
                  fontSize: (theme) => theme.typography.caption.fontSize,
                }}
              >
                {formatValue(value)}
              </Box>
            </Typography>
          ))}
          {resultText && (
            <Box
              component="pre"
              sx={{
                margin: 0,
                marginTop: argEntries.length > 0 ? 0.5 : 0,
                fontSize: (theme) => theme.typography.caption.fontSize,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                color: 'text.secondary',
                fontFamily: 'Menlo, Consolas, monospace',
              }}
            >
              {resultText}
            </Box>
          )}
        </Box>
      )}

      {hasReasoningInLog && !hideInternals ? (
        // Heterogeneous timeline — sub-agent reasoning interleaved with
        // its tool calls in arrival order. Reasoning rows render through
        // the shared Markdown chokepoint so headings, tables, and code
        // spans (which sub-agents stream regularly) display properly.
        // Tool rows reuse the recursive ToolCallCard so per-tool
        // expand/args/result behavior keeps working. With internals hidden
        // (OR-6328) the reasoning rows are contents, not step names — the
        // plain sub-call list below renders instead.
        <Box>
          {timeline.map((entry) =>
            entry.kind === 'reasoning' ? (
              <Box
                key={entry.id}
                sx={{
                  marginLeft: 2.25,
                  marginY: 0.5,
                  paddingLeft: 1,
                  borderLeft: (theme) => `2px solid ${theme.palette.divider}`,
                  color: 'text.secondary',
                }}
                data-testid={`internal-log-reasoning-${entry.id}`}
              >
                <Markdown>{entry.text}</Markdown>
              </Box>
            ) : (
              (() => {
                const sub = subCallsById.get(entry.toolId);
                if (!sub) return null;
                return <ToolCallCard key={sub.toolId} toolCall={sub} depth={depth + 1} />;
              })()
            )
          )}
        </Box>
      ) : (
        (toolCall.subCalls ?? []).length > 0 && (
          <Box>
            {(toolCall.subCalls ?? []).map((sub) => (
              <ToolCallCard
                key={sub.toolId}
                toolCall={sub}
                depth={depth + 1}
                hideInternals={hideInternals}
              />
            ))}
          </Box>
        )
      )}
    </Box>
  );
};
